/**
 * App availability: the derivations behind the per-app tab, the tenant card and the fleet-wide
 * summary. The app catalog splits its records with {@link splitAvailability} (#2568).
 *
 * ## ⛔⛔ THE ONE PROPERTY THIS FILE EXISTS TO HOLD
 *
 * > **A 403 is a fact about the READER. `[]` is a fact about the APP. They must never collapse.**
 *
 * The catalogue is gated `cp:app:read`. Since D5 of 2026-10-20-cp-permissioning the availability
 * read is gated `cp:app:read` on the app too. The catalogue reads each app that the reader holds the
 * key on, site-wide or by a per-app grant (`availabilityReach`), and shows every other availability
 * cell refused. If a refusal classified as *"offered to nobody"*, the catalogue would tell such a
 * reader that **every other app in the fleet is invisible to tenants**: a screen full of confident,
 * wrong alarms, on the exact surface built to make one real alarm findable.
 *
 * ⚠️ That is not a hypothetical inversion of the defect in #2568: it IS the defect, aimed at a
 * different reader. #2568 is "we rendered an app as fine when nobody could see it"; this would be
 * "we rendered every app as invisible when we simply were not allowed to look".
 *
 * ⇒ {@link AppAvailability} is a discriminated union with FIVE arms and no boolean anywhere, so
 * "refused", "failed", "never asked", "in flight" and "offered to nobody" are five values `tsc`
 * will not let a call site conflate.
 */
import type { App } from '../api/apps.api';
import type { AppAvailabilitySummary } from '../api/appAvailabilitySummary.api';
import type { OrgAppLicence } from '../api/appLicences.api';
import { ApiError } from '../platform/http/client';
import { displayMessage } from '../platform/errors';

/**
 * How many apps the tenant overview's availability card asks about.
 *
 * The bound is the console's, not the server's. No route answers availability for every app at
 * once: the read is `GET /v1/apps/:appId/licences`, one app at a time, so the card sends one
 * request per app and nothing on the server bounds that. `TenantOverviewTab` is the one reader. It
 * asks for the first 25 apps through {@link availabilityWindow}, all at once, and names the apps
 * past the window as not checked.
 *
 * The window follows the catalogue order, alphabetical by slug (the producer's
 * `orderBy(asc(apps.slug))`), so it cannot promise that an app with no availability is inside it.
 * Any caption over this window must say so. The app catalog does not read this constant: it asks
 * about every app through its own pool of six.
 */
export const AVAILABILITY_FANOUT_CAP = 25;

/**
 * What we managed to learn about one app's availability.
 *
 * ⛔ Five arms, and every non-`ready` arm is a different reason we do not know. Collapsing any of
 * them into `ready` with an empty list is the defect this file's header describes.
 */
export type AppAvailability =
  | { readonly kind: 'loading' }
  /**
   * The producer answered. ⛔ `live` and `revoked` are SPLIT here rather than at the call site:
   * the read returns both states (D-5 soft revocation) and `rows.length` is the count of records
   * that have ever existed, not the count of tenants the app is offered to today. Splitting once,
   * in the model, is what stops a screen counting a withdrawn record as availability.
   */
  | {
      readonly kind: 'ready';
      readonly live: readonly OrgAppLicence[];
      readonly revoked: readonly OrgAppLicence[];
    }
  /** 403: the principal holds the screen's own grant but not `LICENCES_READ_PERMISSION` on the app. */
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string }
  /** Outside {@link AVAILABILITY_FANOUT_CAP}, or the reader was never going to be allowed to ask. */
  | { readonly kind: 'not-fetched' };

/**
 * Split one answered read into live and withdrawn.
 *
 * ⛔ `revokedAt === null` is the producer's own definition of live — *"null exactly when the
 * licence is live"* — and it is checked against `null` rather than for falsiness: an ISO timestamp
 * is always truthy, but a `!row.revokedAt` written here would also swallow `''`, and the day the
 * producer emits an empty string for something a withdrawn record would count as live.
 */
export function splitAvailability(rows: readonly OrgAppLicence[]): {
  readonly live: readonly OrgAppLicence[];
  readonly revoked: readonly OrgAppLicence[];
} {
  return {
    live: rows.filter((row) => row.revokedAt === null),
    revoked: rows.filter((row) => row.revokedAt !== null),
  };
}

/** An answered read, already split. The one place a 200 becomes an {@link AppAvailability}. */
export function readyAvailability(rows: readonly OrgAppLicence[]): AppAvailability {
  const { live, revoked } = splitAvailability(rows);
  return { kind: 'ready', live, revoked };
}

/**
 * Classify one settled availability read.
 *
 * ⛔⛔ **403 IS ITS OWN ARM. THIS IS THE FUNCTION THE WHOLE FEATURE TURNS ON.**
 *
 * The naive implementation — `catch { return { kind: 'ready', live: [], revoked: [] } }`, or the
 * subtler `catch { return [] }` one layer down — type-checks, renders, and reads as defensive
 * coding. It also makes every non-billing operator's catalogue claim the entire fleet is offered
 * to nobody. `panes/__tests__/availability-model.test.ts` mutates this function to exactly that
 * shape and proves the 403 row goes red.
 *
 * ⚠️ A non-`ApiError` rejection is a network fault or a thrown non-Error: there is no status, and
 * inventing one would claim the server answered when it did not.
 */
export function classifyAvailabilityFailure(cause: unknown): AppAvailability {
  if (cause instanceof ApiError && cause.status === 403) return { kind: 'forbidden' };
  return { kind: 'error', message: displayMessage(cause, 'The availability read failed.') };
}

/**
 * Which apps a screen will ask about.
 *
 * ⛔ Returns the SLICE so the caller can compare it against the full list and caption the
 * difference. A fan-out that caps silently is a screen whose completeness claim is a guess.
 */
export function availabilityWindow(apps: readonly App[]): readonly App[] {
  return apps.slice(0, AVAILABILITY_FANOUT_CAP);
}

/**
 * Availability for every app, by app id.
 *
 * ⛔ **`allSettled`, never `all`.** One refused or failed app must not blank the rest —
 * `Promise.all` rejects on the first failure and would turn a single 403 into a column with no
 * availability at all, which is the collapse this module exists to prevent, arriving through the
 * concurrency primitive instead of through the classifier.
 *
 * ⚠️ Apps outside the window get `not-fetched` rather than being omitted: the row still renders,
 * and *"we never asked"* is a different statement from *"we asked and could not tell"*.
 */
export async function fetchAvailability(
  apps: readonly App[],
  fetchOne: (appId: string) => Promise<readonly OrgAppLicence[]>,
): Promise<ReadonlyMap<string, AppAvailability>> {
  const window = availabilityWindow(apps);
  const settled = await Promise.allSettled(window.map((app) => fetchOne(app.id)));

  const out = new Map<string, AppAvailability>();
  for (const app of apps) out.set(app.id, { kind: 'not-fetched' });
  /*
   * No `settled[i] === undefined` guard. `Promise.allSettled` resolves an array as long as its
   * input, and `forEach` yields only an index in range, so no input reaches such an arm.
   */
  window.forEach((app, i) => {
    const result = settled[i];
    out.set(
      app.id,
      result.status === 'fulfilled'
        ? readyAvailability(result.value)
        : classifyAvailabilityFailure(result.reason),
    );
  });
  return out;
}

/**
 * The verbatim sentence a zero-availability app must carry, wherever it is stated.
 *
 * ⛔ ONE definition for the two APP-SCOPED surfaces that say it — the catalogue cell's reason and
 * the per-app tab's empty state — because two hand-typed copies is two chances for the one sentence
 * that names the defect to drift into two weaker ones.
 *
 * ⚠️ **`TenantPane`'s card deliberately does NOT use it, and that is not drift.** This sentence is
 * about ONE APP (*"this app is deployed but nobody can see it"*); the tenant card's zero state is
 * about ONE TENANT (*"nobody in this organization can see any app"*). Same defect, different
 * subject — and reusing this string there would tell the reader the wrong thing is broken. ⛔ If a
 * third app-scoped surface appears, it uses this constant.
 *
 * ⚠️ It says *"nobody can see it"* rather than *"it is not licensed"* deliberately: the reader of
 * this sentence has just been surprised, and the consequence is what tells them what to do.
 */
export const NOT_OFFERED_SENTENCE =
  'Not offered to any tenant — this app is deployed but nobody can see it.';

/**
 * The tenant-facing noun, inflected by the caller.
 *
 * ⛔ Here rather than at three call sites, for the same reason `catalogueNoun` is in
 * `AppCatalogPane`: *"1 tenants"* is the kind of thing that makes a screen read as unowned.
 */
export function tenantCount(n: number): string {
  return `${n} tenant${n === 1 ? '' : 's'}`;
}

/**
 * The fleet-wide summary's five arms — the same five {@link AppAvailability} carries, for the same
 * reason (#2621).
 *
 * A 403 is a fact about the reader, `notOffered === 0` a fact about the fleet, and no call site may
 * conflate them. `forbidden` covers both routes to that one fact: a failed summary-key
 * pre-check in the console and a server 403 (R-030). `error` carries the producer's sentence, so a
 * failed read never reads as an unmeasured one (R-031). `not-fetched` is a read that was never
 * applicable to this reader. `loading` is a read still in flight. Only `ready` carries a number,
 * and it is the only arm the rail count and the catalog's Not offered tab badge may show as one.
 */
export type AvailabilitySummaryState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly summary: AppAvailabilitySummary }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'not-fetched' };

/**
 * Classify one settled summary read.
 *
 * A fulfilment is `ready` with the two counts. A rejection goes through
 * {@link classifyAvailabilityFailure}, so the 403 rule lives in the one function this file exists
 * to hold: a `forbidden` there is a `forbidden` here, and every other rejection is `error`
 * carrying that function's message. Never `ready` with a zero on any rejection — that collapse is
 * this file's header.
 *
 * It takes a `PromiseSettledResult` rather than a value plus a catch, on {@link fetchAvailability}'s
 * precedent in this file: a rejection is data, and `Promise.allSettled` is what hands it over.
 *
 * On `00-index.md` §4's full-body list, item 7.
 */
export function toSummaryState(
  result: PromiseSettledResult<AppAvailabilitySummary>,
): AvailabilitySummaryState {
  if (result.status === 'fulfilled') return { kind: 'ready', summary: result.value };
  /*
   * `classifyAvailabilityFailure` answers a rejection with `forbidden` or `error` and with nothing
   * else, so these two arms are total over its range. Reading the message off `failure` rather
   * than re-deriving it keeps the fallback sentence in one place.
   */
  const failure = classifyAvailabilityFailure(result.reason);
  return failure.kind === 'error'
    ? { kind: 'error', message: failure.message }
    : { kind: 'forbidden' };
}

/**
 * The rail count's hidden text, inflected: `1 app not offered to any tenant`, `3 apps not offered
 * to any tenant`.
 *
 * Sibling of {@link tenantCount}, and here rather than at the call site for the same reason:
 * "1 apps" is what makes a screen read as unowned. `RailLink` renders the visible digit
 * `aria-hidden` and this sentence in `sr-only`, so a screen reader announces the number once, with
 * its meaning.
 */
export function notOfferedLabel(n: number): string {
  return `${n} app${n === 1 ? '' : 's'} not offered to any tenant`;
}

/**
 * Availability rows, most recently offered first, then by slug.
 *
 * ⛔ The chain is TOTAL — live before withdrawn, then `grantedAt` descending, then slug, then
 * organization id — so the table does not reshuffle between loads. Only the id is unique, so the
 * chain cannot stop at the slug.
 *
 * ⚠️ Live rows sort ABOVE withdrawn ones regardless of date: a withdrawn record is history and a
 * live one is the answer to *"who is offered this today"*, and interleaving them by date would bury
 * today's answer inside last quarter's.
 */
export function sortAvailability(rows: readonly OrgAppLicence[]): readonly OrgAppLicence[] {
  return [...rows].sort(
    (a, b) =>
      Number(a.revokedAt !== null) - Number(b.revokedAt !== null)
      || b.grantedAt.localeCompare(a.grantedAt)
      || a.organizationSlug.localeCompare(b.organizationSlug)
      || a.organizationId.localeCompare(b.organizationId),
  );
}

/**
 * Whether this organization is offered this app right now. Offered, not "can see": a live CP record
 * is the first of two gates, and the tenant's org admin still grants the app's permissions to a
 * role before anyone there sees it (#2621).
 *
 * ⛔⛔ **The comparison is CP-org-id to CP-org-id, and the caller is responsible for that.**
 * `OrgAppLicence.organizationId` is `platform.organizations.id`. The same tenant carries a
 * DIFFERENT id inside each data plane, and a DP org id compared here matches nothing — producing a
 * confident *"not available"* for a tenant who can in fact see the app. ⇒ Callers pass the
 * `:tenantId` route param, which came from `GET /v1/admin/orgs` and is a CP id by construction.
 *
 * ⚠️ Case-insensitive: the producer lower-cases on the POST body but `zod`'s `z.uuid()` accepts the
 * upper-case spelling on the DELETE param, and a uuid that differs only in case is the same row.
 */
export function offeredTo(rows: readonly OrgAppLicence[], cpOrganizationId: string): boolean {
  const wanted = cpOrganizationId.toLowerCase();
  return rows.some((row) => row.revokedAt === null && row.organizationId.toLowerCase() === wanted);
}

/**
 * Which organizations it is worth offering this app to — #2656.
 *
 * ⛔ Organizations that already hold a LIVE record are removed, because offering one is offering a
 * control whose only possible outcome is *"nothing changed"*. ⚠️ Organizations whose record is
 * **withdrawn** stay in, and that is not an oversight: re-offering a withdrawn tenant is the
 * documented use — the producer returns withdrawn rows precisely so *"an operator about to
 * re-license an organization must be able to see that it was revoked before, and when"*.
 *
 * ⛔ Generic over `{ id }` rather than typed to `TenantListItem`, so this shared model does not
 * take a dependency on `tenants.api` for one field. The caller keeps its own row type all the way
 * into the `<option>`, which is what lets the picker render a name it did not have to look up.
 *
 * ⛔⛔ **`id` MUST BE THE CP `platform.organizations.id`.** It is compared against
 * `OrgAppLicence.organizationId` through {@link offeredTo}, and a data-plane org id matches nothing
 * — so a tenant who CAN already see the app would be offered again, and the write would be a
 * confusing 404 from `requireOrganization`. ⇒ the caller `TenantPicker` passes rows from
 * `listLicenceCandidates(appId)` (`GET /v1/apps/:appId/licence-candidates`), whose `id` is the CP id.
 */
export function offerCandidates<T extends { readonly id: string }>(
  organizations: readonly T[],
  rows: readonly OrgAppLicence[],
): readonly T[] {
  return organizations.filter((org) => !offeredTo(rows, org.id));
}

/**
 * What a `POST /v1/apps/:appId/licences` actually did — #2656.
 *
 * ⛔ Three arms because the producer performs three different acts, and a reader needs to be told
 * which one happened. *"You have made this app visible to a tenant who could not see it"*, *"you
 * have restored access somebody withdrew"* and *"you asserted a state that already held"* are not
 * the same sentence, and rendering all three as *"Saved"* throws away the only two that matter.
 */
export type GrantOutcome = 'granted' | 'restored' | 'unchanged';

/**
 * Classify one grant by comparing the producer's answer against the row we already held.
 *
 * ## ⛔⛔ THE RESPONSE BODY CANNOT TELL YOU THIS, SO THE COMPARISON IS THE MEASUREMENT
 *
 * The producer computes `changed` and `reGranted` inside `OrgAppLicenceService.grant()` and puts
 * them in the **audit record only**; `toView()` emits seven fields and neither flag. All three
 * outcomes answer 201 with the same shape. ⇒ there is nothing to read off a single response, and
 * a caller that wants the distinction has to hold a before.
 *
 * ⛔ `before` is the row from the list this pane already displays — the read returns live AND
 * withdrawn rows, so the pre-write state is known without a second request.
 *
 * ## ⚠️ ONE COMPARISON, ON `grantedAt`, AND NO ARM ON `revokedAt`
 *
 * The obvious implementation answers from OUR BELIEF: `before.revokedAt !== null ? 'restored' :
 * 'unchanged'`. The producer's own statement is better. `OrgAppLicenceRepository.grant()`'s
 * `onConflictDoUpdate` CASE expressions set `granted_at = now()` when the existing row is withdrawn
 * and leave it untouched when the row is live, so a MOVED `grantedAt` is the re-grant branch and an
 * unmoved one is the no-op branch, whatever our list believed. ⇒ we compare, and a list that went
 * stale between our read and our write still reports what the producer did.
 *
 * ⚠️ A `before.revokedAt !== null ⇒ 'restored'` short-circuit used to sit before the comparison. It
 * was deleted because it is not falsifiable: its one distinguishing input — a withdrawn `before`
 * whose `grantedAt` did NOT move — is a response the producer cannot emit, since clearing
 * `revoked_at` always sets `granted_at`. An arm no input can distinguish is a claim, not a
 * measurement, and the comparison already answers every producible case.
 *
 * ⚠️ The mirror stale case is absorbed, and correctly: a live row exists on the server that our
 * read missed, so `before` is `undefined`, the write is a no-op, and this returns `granted`. The
 * response carries nothing that could distinguish it, and *"is now offered this app"* stays true as a
 * statement of state — only the implication of change is loose. Callers therefore open the offer
 * flow only off a read that has succeeded, so `before === undefined` means "absent from a real
 * list", never "we never had a list".
 *
 * ⚠️ The same looseness runs the other way, and is likewise accepted (Copilot, PR #2664): a moved
 * `grantedAt` proves the row is NEWER than our `before`, not that THIS request moved it. Hold a
 * withdrawn row, let another operator re-offer it, then submit: the producer takes the live no-op
 * branch and returns the other operator's timestamp, and this says `restored`. The state claim —
 * *"is offered this app again"* — is true; who did it is not something seven fields can carry.
 *
 * ⚠️ `unchanged` is a SUCCESS. The write happened, it was idempotent, and nothing is wrong — so it
 * renders in a neutral tone and never as an error.
 */
export function classifyGrant(
  before: OrgAppLicence | undefined,
  after: OrgAppLicence,
): GrantOutcome {
  if (before === undefined) return 'granted';
  return after.grantedAt === before.grantedAt ? 'unchanged' : 'restored';
}

/**
 * What one tenant is offered, assembled from the per-app reads.
 *
 * ## ⛔⛔ THE NON-ANSWERS ARE COUNTED **SEPARATELY**, AND THAT IS NOT BOOKKEEPING
 *
 * A first cut folded refused, failed, in-flight and never-asked into one `unresolved` number. It
 * type-checked and it reproduced this feature's own defect one level over: a server 403 rendered
 * as *"Checking 2 of 2 apps…"* **forever**, with no grant named and no remedy — an unreadable
 * state dressed as an in-flight one. Worse, a tenant with a genuinely empty availability set and
 * more than {@link AVAILABILITY_FANOUT_CAP} apps could never reach the alarm at all, because the
 * tail of never-asked apps held `unresolved > 0` permanently. ⇒ **that is the #2568 state,
 * suppressed by the screen built to surface it.**
 *
 * ⇒ Four counts, because they are four different sentences a reader needs: *"you may not look"*,
 * *"it broke"*, *"still asking"* and *"we never asked"*.
 */
export interface TenantAvailability {
  /** The apps this organization is offered today, in the order the catalogue returned them. */
  readonly offered: readonly App[];
  /** Still in flight, or not yet begun. The only arm that will change on its own. */
  readonly pending: number;
  /**
   * The server refused the read (403).
   *
   * The tenant card pre-checks `LICENCES_READ_PERMISSION` site-wide, so in practice this is
   * all-or-nothing: a principal is refused every app or none. A card may therefore treat
   * `refused > 0` as *"refused"* outright.
   */
  readonly refused: number;
  /** The read failed. {@link TenantAvailability.firstError} carries the producer's sentence. */
  readonly failed: number;
  /** Outside {@link AVAILABILITY_FANOUT_CAP}. Never asked, so nothing is known. */
  readonly notFetched: number;
  /**
   * ⛔ Everything that is not an answer — `pending + refused + failed + notFetched`. Kept as a
   * derived total so a card that only needs *"is this list complete"* does not re-add four numbers
   * and get it wrong. **Never the only thing a card renders**; see this interface's docblock.
   */
  readonly unresolved: number;
  /**
   * The first failure's message, or `null` when nothing failed.
   *
   * ⛔ The FIRST rather than a join: the fan-out is one endpoint under one grant, so N failures are
   * almost always N copies of one sentence, and concatenating them would fill a card with the same
   * words repeated. ⚠️ `failed` carries the count, so the reader is not told one app broke when
   * eleven did.
   */
  readonly firstError: string | null;
  /** How many apps were considered at all. `0` means the catalogue itself was empty or unread. */
  readonly total: number;
}

/**
 * Invert the app-keyed availability map into *"which apps can this tenant see"*.
 *
 * ## ⛔⛔ THE INVERSION EXISTS BECAUSE THERE IS NO ORG-KEYED ENDPOINT
 *
 * The producer is `GET /v1/apps/:appId/licences` — **app-keyed**. There is no
 * `GET /v1/orgs/:orgId/licences` and none is added here (a new producer route is backend work and
 * outside this change). ⇒ a tenant-side view is a fan-out over the catalogue plus this filter, and
 * {@link AVAILABILITY_FANOUT_CAP} is what bounds it.
 *
 * ⛔⛔ **`cpOrganizationId` MUST BE THE CONTROL-PLANE ORGANIZATION UUID.** `platform.organizations.id`
 * is the FK target of `release.org_app_licences.organization_id`; the SAME tenant carries a
 * different id inside each data plane (measured: `88398fbb-…` for `witness-ai` in the CP,
 * `78945b43-…` in the DP). A DP org id passed here matches nothing and renders as *"this tenant can
 * see no apps"* — the alarm state, fabricated. ⇒ callers pass the `/platform/tenants/:tenantId`
 * route param, which came from `GET /v1/admin/orgs` and is a CP id by construction. ⛔ Never pass
 * an id lifted out of a data-plane payload.
 */
export function tenantAvailability(
  apps: readonly App[],
  availability: ReadonlyMap<string, AppAvailability>,
  cpOrganizationId: string,
): TenantAvailability {
  const offered: App[] = [];
  let pending = 0;
  let refused = 0;
  let failed = 0;
  let notFetched = 0;
  let firstError: string | null = null;

  for (const [index, app] of apps.entries()) {
    /*
     * ⛔ `undefined` — the fan-out has not landed yet — is PENDING, never "not offered".
     *
     * ⛔⛔ …BUT ONLY INSIDE THE WINDOW. An app past {@link AVAILABILITY_FANOUT_CAP} is never going
     * to be requested, so calling it `loading` announces work that will not happen: with a map
     * that has not landed yet, the card said *"Checking 27 of 27 apps…"* on a catalogue where it
     * was about to ask about 25 and stop. Found by Copilot on PR #2586.
     *
     * ⚠️ FIXED HERE RATHER THAN BY SEEDING THE CALLER'S STATE, which was the first attempt and was
     * wrong: a seed written in an effect lands one render AFTER the catalogue arrives, so the
     * false count still rendered once. The window is a property of the model, so the model is
     * where an absent entry gets its correct default — no caller can mis-time it.
     */
    const found =
      availability.get(app.id) ??
      ({ kind: index < AVAILABILITY_FANOUT_CAP ? 'loading' : 'not-fetched' } as const);
    switch (found.kind) {
      case 'ready':
        if (offeredTo(found.live, cpOrganizationId)) offered.push(app);
        break;
      case 'forbidden':
        refused += 1;
        break;
      case 'error':
        failed += 1;
        firstError = firstError ?? found.message;
        break;
      case 'not-fetched':
        notFetched += 1;
        break;
      default:
        // ⛔ `loading`. An exhaustive `switch` on the union, so a sixth arm is a compile error here
        // rather than a state that silently lands in whichever bucket a chain of `if`s ended on.
        pending += 1;
    }
  }

  return {
    offered,
    pending,
    refused,
    failed,
    notFetched,
    unresolved: pending + refused + failed + notFetched,
    firstError,
    total: apps.length,
  };
}
